import type { LogMeta } from './types'

const MAX_DEPTH = 10
const MIN_PARTIAL_LENGTH = 12
const MIN_PII_PARTIAL_LENGTH = 6
const MAX_STAR_RUN = 8

export const SECRET_KEY_PATTERN =
  /password|passwd|secret|token|apikey|api[_-]key|authorization|credential|bearer|x[_-]?api[_-]?key|priv(?:ate)?[_-]?key|client[_-]?secret|refresh[_-]?token|access[_-]?token|cookie/i

export const SECRET_VALUE_PATTERNS: ReadonlyArray<RegExp> = [
  // JWT (three base64url segments)
  /\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,}\b/g,
  // AWS Access Key ID
  /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g,
  // Stripe live/test keys (sk_, rk_, pk_)
  /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{16,}\b/g,
  // GitHub tokens (ghp_, ghs_, gho_, ghu_, ghr_)
  /\bgh[pousr]_[A-Za-z0-9]{36,}\b/g,
  // Slack tokens
  /\bxox[abprs]-[\w-]{10,}\b/g,
  // Google API key
  /\bAIza[\w-]{35}\b/g,
  // Twilio API key SID
  /\bSK[a-f0-9]{32}\b/g,
  // Bearer/Basic auth headers: mask only the secret, keep the "Bearer "/"Basic " word.
  // Named groups make the redactString contract explicit — the `prefix` group is kept
  // verbatim and the `secret` group is masked. Avoid lookbehind here: it's a SyntaxError
  // on Safari < 16.4 and can't be transpiled, which blanks the whole app. Named capture
  // groups (?<name>…) are safe (Safari 11.1+); only lookbehind is the hazard.
  /(?<prefix>\bBearer\s+)(?<secret>[\w.~+/=-]{20,})/g,
  /(?<prefix>\bBasic\s+)(?<secret>[A-Za-z0-9+/=]{16,})/g
]

// Key names that hold PII values (matched against full key name, case-insensitive)
export const PII_KEY_PATTERN =
  /^(e[-_]?mail|phone|mobile|cell|fax|first[-_]?name|last[-_]?name|full[-_]?name|contact[-_]?name|display[-_]?name|owner[-_]?name|owner[-_]?email|customer[-_]?name|customer[-_]?email)$/i

// Value patterns for PII detected inline in freeform strings
export const PII_VALUE_PATTERNS: ReadonlyArray<RegExp> = [
  // Email addresses
  /\b[\w.%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
  // NA phone: (555) 123-4567 with optional +1 prefix
  /\+?1?[-.\s]?\([2-9]\d{2}\)\s?\d{3}[-.\s]?\d{4}/g,
  // NA phone: 555-123-4567, 555.123.4567, 5551234567 with optional +1 prefix
  /\b\+?1?[-.\s]?[2-9]\d{2}[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
  // International E.164 phone numbers
  /\b\+[1-9]\d{6,14}\b/g
]

export function mask(value: string): string {
  if (value.length < MIN_PARTIAL_LENGTH) {
    return '***'
  }
  const head = value.slice(0, 4)
  const tail = value.slice(-4)
  const middleLen = Math.min(value.length - 8, MAX_STAR_RUN)
  return `${head}${'*'.repeat(middleLen)}${tail}`
}

export function maskPii(value: string): string {
  if (value.length < MIN_PII_PARTIAL_LENGTH) {
    return '***'
  }
  const head = value.slice(0, 2)
  const tail = value.slice(-2)
  const middleLen = Math.min(value.length - 4, MAX_STAR_RUN)
  return `${head}${'*'.repeat(middleLen)}${tail}`
}

export function redactString(input: string, skipPii?: boolean): string {
  if (!input) return input
  let out = input
  for (const pattern of SECRET_VALUE_PATTERNS) {
    out = out.replace(pattern, (...args) => {
      const match = args[0] as string
      // When a pattern uses named groups, String.replace passes the groups object as the
      // final callback argument. Patterns with a `secret` group keep their `prefix` group
      // verbatim and mask only the secret; all other patterns mask the whole match. This
      // keeps the redaction contract explicit rather than relying on argument arity.
      const last = args[args.length - 1]
      const groups = last && typeof last === 'object' ? (last as { prefix?: string; secret?: string }) : undefined
      if (groups?.secret != null) {
        return `${groups.prefix ?? ''}${mask(groups.secret)}`
      }
      return mask(match)
    })
  }
  if (!skipPii) {
    for (const pattern of PII_VALUE_PATTERNS) {
      out = out.replace(pattern, (match) => maskPii(match))
    }
  }
  return out
}

export function redactError(err: unknown, skipPii?: boolean): unknown {
  if (!(err instanceof Error)) return err

  const newMessage = redactString(err.message, skipPii)
  const newStack = err.stack ? redactString(err.stack, skipPii) : err.stack

  if (newMessage === err.message && newStack === err.stack) {
    return err
  }

  const cloned = new Error(newMessage)
  cloned.name = err.name
  if (newStack) cloned.stack = newStack
  for (const key of Object.keys(err)) {
    if (key !== 'message' && key !== 'stack' && key !== 'name') {
      ;(cloned as unknown as Record<string, unknown>)[key] = (err as unknown as Record<string, unknown>)[key]
    }
  }
  return cloned
}

function redactValue(
  value: unknown,
  forceMask: boolean,
  forcePiiMask: boolean,
  skipPii: boolean,
  seen: WeakSet<object>,
  depth: number
): unknown {
  if (depth > MAX_DEPTH) return '[REDACTED:DEPTH]'
  if (value === null || value === undefined) return value

  if (typeof value === 'string') {
    if (forceMask) return mask(value)
    if (forcePiiMask && !skipPii) return maskPii(value)
    return redactString(value, skipPii)
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    if (forceMask) return '[REDACTED]'
    if (forcePiiMask && !skipPii) return '[REDACTED]'
    return value
  }

  if (typeof value !== 'object') {
    return value
  }

  if (value instanceof Error) {
    return redactError(value, skipPii)
  }

  if (value instanceof Date || value instanceof RegExp) {
    return value
  }

  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  if (Array.isArray(value)) {
    return value.map((v) => redactValue(v, forceMask, forcePiiMask, skipPii, seen, depth + 1))
  }

  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const childForceSecret = forceMask || SECRET_KEY_PATTERN.test(k)
    const childForcePii = forcePiiMask || PII_KEY_PATTERN.test(k)
    out[k] = redactValue(v, childForceSecret, childForcePii, skipPii, seen, depth + 1)
  }
  return out
}

export function redactMeta(meta?: LogMeta, skipPii?: boolean): LogMeta | undefined {
  if (!meta) return meta
  const seen = new WeakSet<object>()
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(meta)) {
    // Internal flags pass through untouched (sentry, index, skipPiiMasking)
    if (k === 'sentry' || k === 'index' || k === 'skipPiiMasking') {
      out[k] = v
      continue
    }
    if (k === 'error') {
      out.error = redactError(v, skipPii)
      continue
    }
    const forceSecret = SECRET_KEY_PATTERN.test(k)
    const forcePii = PII_KEY_PATTERN.test(k)
    out[k] = redactValue(v, forceSecret, forcePii, skipPii ?? false, seen, 1)
  }
  return out as LogMeta
}
